/**
 * Register — ELF-CIRCLE.
 *
 * Presentation only: the spam protection (honeypot, dwell time, submit
 * throttle, interaction count), the validation and the register call are the
 * template's, unchanged. See ElfAuthShell for the two-panel chrome.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Briefcase, ArrowRight, Loader2, Eye, EyeOff, Check } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { ElfAuthShell } from '../components';
import { route } from '../../../shared/utils';

type AccountType = 'user' | 'client';

const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const [accountType, setAccountType] = useState<AccountType>('user');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    honeypot: '', // Spam protection - hidden field
  });

  // Spam protection state
  const [formStartTime, setFormStartTime] = useState<number>(0);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const [interactionCount, setInteractionCount] = useState<number>(0);

  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);

    if (isSubmitting) return;

    const currentTime = Date.now();
    const timeSinceStart = currentTime - formStartTime;
    const timeSinceLastSubmit = currentTime - lastSubmitTime;

    // Bot filled the hidden field.
    if (formData.honeypot) {
      setRegisterError('Please try again later');
      return;
    }
    // Submitted faster than a person could read the form.
    if (timeSinceStart < 5000) {
      setRegisterError('Please take your time to fill out the form');
      return;
    }
    if (lastSubmitTime > 0 && timeSinceLastSubmit < 30000) {
      const remainingTime = Math.ceil((30000 - timeSinceLastSubmit) / 1000);
      setRegisterError(`Please wait ${remainingTime} seconds before trying again`);
      return;
    }
    if (interactionCount < 3) {
      setRegisterError('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    setLastSubmitTime(currentTime);

    try {
      if (formData.password !== formData.confirmPassword) {
        setRegisterError('Passwords do not match');
        return;
      }
      if (formData.password.length < 8) {
        setRegisterError('Password must be at least 8 characters long');
        return;
      }
      if (!formData.agreeToTerms) {
        setRegisterError('You must agree to the terms and conditions');
        return;
      }

      // The server clamps role/level to an allowlist regardless
      // (auth.service.ts) — this only selects between the two
      // self-registerable account types.
      const result = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: accountType,
        level: 'basic',
      });

      if (result.success) setIsSuccess(true);
      else setRegisterError(result.error || 'Registration failed');
    } catch (error) {
      setRegisterError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    // Interaction count feeds the spam check; the honeypot must not raise it.
    if (name !== 'honeypot' && type !== 'checkbox') {
      setInteractionCount((prev) => prev + 1);
    }
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <>
      <SEO
        title="Request access"
        description="Create an ELF-CIRCLE account."
        keywords="register, sign up, elf-circle"
      />

      <ElfAuthShell
        kicker={isSuccess ? 'Almost there' : 'Request access'}
        title={isSuccess ? 'Check your email' : 'Join the circle'}
        lede={
          isSuccess
            ? undefined
            : 'Members and clients both start here. Verification takes a minute.'
        }
        artHeadline="Built for artists, models and music creators"
        artLede="One account for consultations, bookings and everything that follows."
      >
        {isSuccess ? (
          <div className="elf-auth__done">
            <span className="elf-auth__tick" aria-hidden="true">
              <Check />
            </span>
            <p className="elf-auth__note">
              Your account is created. We&rsquo;ve sent a verification link to{' '}
              <strong>{formData.email}</strong> — open it to activate the account, then sign in.
            </p>
            <Link className="elf-auth__ghost" to={route('/auth/login')}>
              Back to sign in
              <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="elf-auth__form" noValidate>
            {registerError && (
              <div className="elf-auth__err" role="alert">
                {registerError}
              </div>
            )}

            {/* Admin accounts are never self-registered — they are provisioned
                by seed or by an existing admin. */}
            <div className="elf-auth__field">
              <span>I&rsquo;m signing up as</span>
              <div className="elf-auth__seg">
                <button
                  type="button"
                  className="elf-auth__segbtn"
                  onClick={() => setAccountType('user')}
                  aria-pressed={accountType === 'user'}
                >
                  <User aria-hidden="true" />
                  Member
                </button>
                <button
                  type="button"
                  className="elf-auth__segbtn"
                  onClick={() => setAccountType('client')}
                  aria-pressed={accountType === 'client'}
                >
                  <Briefcase aria-hidden="true" />
                  Client
                </button>
              </div>
            </div>

            <label className="elf-auth__field">
              <span>Full name</span>
              <input
                name="name"
                type="text"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your name"
              />
            </label>

            <label className="elf-auth__field">
              <span>Email</span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder="you@company.com"
              />
            </label>

            <label className="elf-auth__field">
              <span>Phone — optional</span>
              <input
                name="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </label>

            <label className="elf-auth__field">
              <span>Password</span>
              <span className="elf-auth__pwwrap">
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="elf-auth__peek"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </span>
            </label>

            <label className="elf-auth__field">
              <span>Confirm password</span>
              <span className="elf-auth__pwwrap">
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Type it again"
                />
                <button
                  type="button"
                  className="elf-auth__peek"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </span>
            </label>

            {/* Hidden from people, offered to bots. */}
            <input
              name="honeypot"
              type="text"
              value={formData.honeypot}
              onChange={handleInputChange}
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            <label className="elf-auth__terms">
              <input
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
              />
              <span>
                I agree to the{' '}
                <Link className="elf-auth__link" to={route('/terms')} target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link className="elf-auth__link" to={route('/privacy')} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </span>
            </label>

            <button type="submit" className="pillcta elf-auth__submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating account…' : 'Create account'}
              <span aria-hidden="true">
                {isSubmitting ? <Loader2 className="elf-auth__spin" /> : <ArrowRight />}
              </span>
            </button>

            <p className="elf-auth__alt">
              Already have an account?{' '}
              <Link className="elf-auth__link" to={route('/auth/login')}>
                Sign in
              </Link>
            </p>
          </form>
        )}
      </ElfAuthShell>
    </>
  );
};

export default RegisterPage;
